import { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary.tsx";
import { Masthead, loadHelpDialog } from "@/components/Masthead.tsx";
import { LoadingLeaf } from "@/components/Notices.tsx";
import { OfflineBanner } from "@/components/OfflineBanner.tsx";
import { useApplyTheme } from "@/components/ThemeToggle.tsx";
import { useReminderSync } from "@/lib/useReminder.ts";
import { useSyncLifecycle } from "@/lib/sync/useSync.ts";
import { GamePage } from "@/pages/GamePage.tsx";
import { HomePage } from "@/pages/HomePage.tsx";
import { useHelp } from "@/lib/help.ts";
import { lazyNamed, prefetchWhenIdle } from "@/lib/lazyLoad.ts";
import { usePwa } from "@/lib/pwa.ts";
import { useInstallIntroOpen } from "@/lib/useInstallIntro.ts";

// The daily game (/ and /challenges/:n) is in the first download. Everything else loads the first time it's needed.
const loadProgress = () => import("@/pages/ProgressPage.tsx");
const loadArchive = () => import("@/pages/ArchivePage.tsx");
const ArchivePage = lazyNamed(loadArchive, "ArchivePage");
const ProgressPage = lazyNamed(loadProgress, "ProgressPage");
const SendPage = lazyNamed(() => import("@/pages/SendPage.tsx"), "SendPage");
const SentPage = lazyNamed(() => import("@/pages/SentPage.tsx"), "SentPage");
const SyncPage = lazyNamed(() => import("@/pages/SyncPage.tsx"), "SyncPage");
const PrivacyPage = lazyNamed(() => import("@/pages/PrivacyPage.tsx"), "PrivacyPage");
const UpdateNotice = lazyNamed(() => import("@/components/UpdateNotice.tsx"), "UpdateNotice");
const InstallIntro = lazyNamed(() => import("@/components/InstallIntro.tsx"), "InstallIntro");

/** `/changelog` is a link you can share or bookmark: it opens the app with the "What's new" tab showing. */
function OpenWhatsNew() {
  useEffect(() => useHelp.getState().show("new"), []);
  return <Navigate to="/" replace />;
}

function GameRoute() {
  const n = Number(useParams().n);
  if (!Number.isInteger(n) || n < 1) return <Navigate to="/" replace />;
  return <GamePage key={n} number={n} />;
}

/** What shows for the moment a page's code is loading: the masthead and the same skeleton the game uses. */
function PageLoading() {
  return (
    <div className="min-h-dvh">
      <Masthead />
      <LoadingLeaf />
    </div>
  );
}

export function App() {
  useApplyTheme();
  useReminderSync();
  useSyncLifecycle();
  const updateReady = usePwa((s) => s.updateReady);
  const introOpen = useInstallIntroOpen();
  // Once the first screen is up and the browser is idle, fetch the pieces players open most, so they open instantly.
  useEffect(() => prefetchWhenIdle([loadHelpDialog, loadProgress, loadArchive]), []);
  return (
    <>
      {updateReady && (
        <Suspense fallback={null}>
          <UpdateNotice />
        </Suspense>
      )}
      <OfflineBanner />
      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/challenges" element={<ArchivePage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/changelog" element={<OpenWhatsNew />} />
            <Route path="/send" element={<SendPage />} />
            <Route path="/sync" element={<SyncPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/v/:code" element={<SentPage />} />
            <Route path="/challenges/:n" element={<GameRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
      {introOpen && (
        <Suspense fallback={null}>
          <InstallIntro open />
        </Suspense>
      )}
    </>
  );
}
